import React, { useState, useEffect } from 'react';
import inventoryService from '../services/inventoryService';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';
import {
  Plus, Trash2, X, Edit2, Search, CheckSquare, Square,
  Palette, Power, PowerOff
} from 'lucide-react';

const CoresManager = () => {
  const [loading, setLoading] = useState(false);
  const [cores, setCores] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCor, setEditingCor] = useState(null);
  const [selectedCores, setSelectedCores] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [confirmInactivate, setConfirmInactivate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmInactivateSelected, setConfirmInactivateSelected] = useState(false);
  const [pendingId, setPendingId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    hex_color: '',
    ativo: true
  });

  useEffect(() => {
    loadCores();
  }, []);

  const loadCores = async () => {
    setLoading(true);
    try {
      const params = {};
      if (showInactive) {
        params.ativo = 'all';
      }
      const response = await inventoryService.getCores(params);
      if (response.success) {
        setCores(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar cores:', error);
      toast.error('Erro ao carregar cores');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadCores();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nome) {
      toast.error('Nome da cor é obrigatório');
      return;
    }

    try {
      if (editingCor) {
        await inventoryService.updateCor(editingCor.id, formData);
        toast.success('Cor atualizada com sucesso!');
      } else {
        await inventoryService.createCor(formData);
        toast.success('Cor criada com sucesso!');
      }

      resetForm();
      loadCores();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar cor');
    }
  };

  const handleEdit = (cor) => {
    setEditingCor(cor);
    setFormData({
      nome: cor.nome || '',
      descricao: cor.descricao || '',
      hex_color: cor.hex_color || '',
      ativo: cor.ativo
    });
    setShowForm(true);
  };

  // Inativar cor
  const handleInactivate = async (id) => {
    setPendingId(id);
    setConfirmInactivate(true);
  };

  const confirmInactivateAction = async () => {
    setConfirmInactivate(false);
    try {
      await inventoryService.deleteCor(pendingId);
      toast.success('Cor inativada com sucesso!');
      loadCores();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao inativar cor');
    }
  };

  // Ativar cor
  const handleActivate = async (id) => {
    try {
      await inventoryService.updateCor(id, { ativo: true });
      toast.success('Cor ativada com sucesso!');
      loadCores();
    } catch (error) {
      toast.error('Erro ao ativar cor');
    }
  };

  // Excluir definitivamente
  const handleDelete = async (id) => {
    setPendingId(id);
    setConfirmDelete(true);
  };

  const confirmDeleteAction = async () => {
    setConfirmDelete(false);
    try {
      await inventoryService.deleteCor(pendingId);
      toast.success('Cor excluída com sucesso!');
      loadCores();
    } catch (error) {
      const msg = error.response?.data?.message || 'Erro ao excluir cor';
      toast.error(msg);
    }
  };

  // Seleção individual
  const toggleSelectCor = (id) => {
    setSelectedCores(prev =>
      prev.includes(id)
        ? prev.filter(c => c !== id)
        : [...prev, id]
    );
  };

  // Seleção todos
  const toggleSelectAll = () => {
    if (selectedCores.length === filteredCores.length) {
      setSelectedCores([]);
    } else {
      setSelectedCores(filteredCores.map(c => c.id));
    }
  };

  // Inativar selecionados
  const handleInactivateSelected = async () => {
    if (selectedCores.length === 0) {
      toast.error('Nenhuma cor selecionada');
      return;
    }

    setConfirmInactivateSelected(true);
  };

  const confirmInactivateSelectedAction = async () => {
    setConfirmInactivateSelected(false);
    try {
      setLoading(true);
      for (const id of selectedCores) {
        await inventoryService.deleteCor(id);
      }
      toast.success(`${selectedCores.length} cor(es) inativada(s) com sucesso!`);
      setSelectedCores([]);
      loadCores();
    } catch (error) {
      toast.error('Erro ao inativar cores');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar cores
  const filteredCores = cores.filter(cor => {
    const matchesSearch = !search ||
      cor.nome.toLowerCase().includes(search.toLowerCase()) ||
      (cor.descricao && cor.descricao.toLowerCase().includes(search.toLowerCase()));

    if (showInactive) {
      return matchesSearch;
    }
    return matchesSearch && cor.ativo;
  });

  const resetForm = () => {
    setShowForm(false);
    setEditingCor(null);
    setFormData({
      nome: '',
      descricao: '',
      hex_color: '',
      ativo: true
    });
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cores</h1>
          <p className="text-gray-600">Gerencie as cores de produtos</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Nova Cor
        </button>
      </div>

      {/* Search and Actions */}
      <div className="mb-6 flex gap-4 flex-wrap">
        <form onSubmit={handleSearch} className="flex-1 flex gap-4 min-w-[300px]">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nome ou descrição..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
            Buscar
          </button>
        </form>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded"
          />
          <span className="text-sm text-gray-700">Mostrar inativas</span>
        </label>

        {selectedCores.length > 0 && (
          <button
            onClick={handleInactivateSelected}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            <PowerOff className="w-4 h-4" />
            Inativar ({selectedCores.length})
          </button>
        )}
      </div>

      {/* Cores List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={toggleSelectAll}
                  className="text-gray-500 hover:text-gray-700"
                  title={selectedCores.length === filteredCores.length ? "Desmarcar todos" : "Selecionar todos"}
                >
                  {selectedCores.length === filteredCores.length && filteredCores.length > 0 ? (
                    <CheckSquare className="w-5 h-5" />
                  ) : (
                    <Square className="w-5 h-5" />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Descrição
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  Carregando...
                </td>
              </tr>
            ) : filteredCores.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  Nenhuma cor encontrada
                </td>
              </tr>
            ) : (
              filteredCores.map((cor) => (
                <tr key={cor.id} className={`hover:bg-gray-50 ${selectedCores.includes(cor.id) ? 'bg-blue-50' : ''} ${!cor.ativo ? 'bg-gray-100' : ''}`}>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => toggleSelectCor(cor.id)}
                      className="text-gray-500 hover:text-blue-600"
                    >
                      {selectedCores.includes(cor.id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {cor.hex_color && (
                        <div
                          className="w-6 h-6 rounded-full mr-3 border border-gray-300"
                          style={{ backgroundColor: cor.hex_color }}
                        />
                      )}
                      <span className="text-sm font-medium text-gray-900">{cor.nome}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {cor.descricao || '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      cor.ativo
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {cor.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {cor.ativo ? (
                        <button
                          onClick={() => handleInactivate(cor.id)}
                          className="text-orange-600 hover:text-orange-800 p-1"
                          title="Inativar"
                        >
                          <PowerOff className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleActivate(cor.id)}
                          className="text-green-600 hover:text-green-800 p-1"
                          title="Ativar"
                        >
                          <Power className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(cor)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Editar"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(cor.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Excluir definitivamente"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {editingCor ? 'Editar Cor' : 'Nova Cor'}
              </h2>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da Cor *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Azul, Vermelho, Verde"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cor (Hexadecimal)
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.hex_color || '#000000'}
                    onChange={(e) => setFormData({ ...formData, hex_color: e.target.value })}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.hex_color || ''}
                    onChange={(e) => setFormData({ ...formData, hex_color: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="#000000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Descrição adicional da cor..."
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={formData.ativo}
                  onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="ativo" className="ml-2 text-sm text-gray-700">
                  Cor ativa
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingCor ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmInactivate}
        onConfirm={confirmInactivateAction}
        onCancel={() => setConfirmInactivate(false)}
        title="Inativar Cor"
        message="Tem certeza que deseja inativar esta cor?"
        confirmText="Inativar"
        type="warning"
      />

      <ConfirmModal
        show={confirmDelete}
        onConfirm={confirmDeleteAction}
        onCancel={() => setConfirmDelete(false)}
        title="Excluir Cor"
        message="ATENÇÃO: Exclusão definitiva! A cor será removida permanentemente. Continuar?"
        confirmText="Excluir"
        type="danger"
      />

      <ConfirmModal
        show={confirmInactivateSelected}
        onConfirm={confirmInactivateSelectedAction}
        onCancel={() => setConfirmInactivateSelected(false)}
        title="Inativar Cores"
        message={`Tem certeza que deseja inativar ${selectedCores.length} cor(es)?`}
        confirmText="Inativar"
        type="warning"
      />
    </div>
  );
};

export default CoresManager;
